import dayjs from 'dayjs';
import { LanguageDropdown } from '../LanguageDropdown';
import styles from './LoginContainer.module.css';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { KioskFooter } from '@/components/KioskComponents/KioskFooter';

type TProps = {
  background?: string;
  showKioskFooter?: boolean;
  mobileLogo?: string;
  /**
   * The wrapping element to render as. Defaults to `div`. Pass `main` when the
   * container is the top-level landmark for the page (e.g. standalone login and
   * activation). In kiosk the `<main>` is provided by KioskLayout, so it stays
   * a `div` there to avoid nested landmarks.
   */
  as?: 'div' | 'main';
};

/**
 * LoginContainer component providing a layout for authentication pages.
 * Displays a form with language selection and a background image.
 * @param props - Component props.
 * @param props.children - Content to be displayed inside the form block.
 * @param props.background - Path to the background image.
 * @param props.showKioskFooter - a variable that determines whether to display the kiosk footer or not
 * @param props.mobileLogo - optional institution logo shown above the content on mobile screens.
 * @param props.as - wrapping element, `div` by default; pass `main` when the container is the top-level page landmark.
 * @returns JSX element for the login container layout.
 */
export const LoginContainer = ({
  children,
  background = '/images/auth_bg.png',
  showKioskFooter = false,
  mobileLogo,
  as: Wrapper = 'div',
}: PropsWithChildren<TProps>) => {
  const { t } = useTranslation();

  const copyrightText = t('globalmed-copyright', {
    currentYear: dayjs().year(),
  });

  return (
    <Wrapper className={styles.main}>
      <div className={styles.form}>
        <div className={styles.formCenter}>
          {mobileLogo && (
            <img
              className={styles.mobileLogo}
              src={mobileLogo}
              alt={t('institution-logo')}
            />
          )}
          <div className={styles.formBlock}>
            <LanguageDropdown />
            {children}
          </div>
        </div>
        {showKioskFooter ? (
          <KioskFooter text={copyrightText} variant="absolute" />
        ) : (
          <div className={styles.bottom}>{copyrightText}</div>
        )}
      </div>
      <div className={styles.image}>
        <img
          className={styles.imageImg}
          src={background}
          alt=""
          data-testid="login-cover-image"
        />
      </div>
    </Wrapper>
  );
};
